import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  FaProjectDiagram,
  FaMapMarkedAlt,
  FaFileAlt,
  FaComments,
  FaHandPointer,
  FaBrain,
  FaLink,
  FaFilter,
  FaArrowRight,
} from 'react-icons/fa';
import { FEATURES_PAGE } from '../config/content';
import styles from './Features.module.css';
import SEO from '../components/SEO';

/* Icon map to resolve icon names from meta.json */
const iconMap: Record<string, React.ElementType> = {
  FaProjectDiagram,
  FaMapMarkedAlt,
  FaFileAlt,
  FaComments,
  FaHandPointer,
  FaBrain,
  FaLink,
  FaFilter,
};

interface FeatureMeta {
  id: string;
  title: string;
  shortDescription: string;
  icon: string;
  order: number;
}

/* ------------------------------------------------------------------ */
/*  Reusable reveal wrapper (matches Home pattern)                     */
/* ------------------------------------------------------------------ */
const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className, id }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const Features: React.FC = () => {
  const [features, setFeatures] = useState<FeatureMeta[]>([]);
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const [featureHtml, setFeatureHtml] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Load feature manifest
  useEffect(() => {
    fetch('/data/features/manifest.json')
      .then((res) => res.json())
      .then((data: FeatureMeta[]) => {
        setFeatures(data);
        if (data.length > 0) {
          setActiveFeature(data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // Load feature content when active feature changes
  useEffect(() => {
    if (!activeFeature) return;
    setLoadingContent(true);
    fetch(`/data/features/${activeFeature}/content.html`)
      .then((res) => res.text())
      .then((html) => {
        setFeatureHtml(html);
        setLoadingContent(false);
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      })
      .catch(() => {
        setFeatureHtml(`<p>${FEATURES_PAGE.contentNotAvailable}</p>`);
        setLoadingContent(false);
      });
  }, [activeFeature]);

  const activeFeatureMeta = features.find((f) => f.id === activeFeature);

  return (
    <main className={styles.page}>
      <SEO path="/features" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ====== HERO BANNER ====== */}
      <div className={styles.heroWrapper}>
        <motion.div
          className={styles.heroInner}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className={styles.heroContent}>
            <div className={styles.heroText}>
              <div className={styles.heroBadge}>
                <div className={styles.badgeDot} />
                <span>{FEATURES_PAGE.hero.label}</span>
              </div>
              <h1 className={styles.sectionTitle}>
                {FEATURES_PAGE.hero.title}<em>{FEATURES_PAGE.hero.titleAccent}</em>
              </h1>
              <p className={styles.sectionSub}>{FEATURES_PAGE.hero.subtitle}</p>
            </div>
            <div className={styles.heroImageWrap}>
              <img
                src="/images/features/hero-banner.png"
                alt="Features"
                className={styles.heroImage}
              />
            </div>
          </div>
        </motion.div>
      </div>

      <div className={styles.sectionDivider} />

      {/* ====== FEATURES EXPLORER ====== */}
      <section className={styles.section}>
        <Reveal className={styles.explorerLayout}>
          {/* Left Panel — Feature List */}
          <div className={styles.featureList}>
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon] || FaFileAlt;
              const isActive = activeFeature === feature.id;
              return (
                <button
                  key={feature.id}
                  className={`${styles.featureListItem} ${isActive ? styles.featureListItemActive : ''}`}
                  onClick={() => setActiveFeature(feature.id)}
                >
                  <div className={`${styles.featureListIcon} ${isActive ? styles.featureListIconActive : ''}`}>
                    <IconComponent />
                  </div>
                  <div className={styles.featureListText}>
                    <span className={styles.featureListTitle}>{feature.title}</span>
                    <span className={styles.featureListDesc}>{feature.shortDescription}</span>
                  </div>
                  {isActive && <div className={styles.activeIndicator} />}
                </button>
              );
            })}
          </div>

          {/* Right Panel — Feature Content */}
          <div className={styles.featureContent} ref={contentRef}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {loadingContent ? (
                  <div className={styles.featureLoading}>
                    <div className={styles.loadingDot} />
                    <div className={styles.loadingDot} />
                    <div className={styles.loadingDot} />
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: featureHtml }} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ====== CTA BANNER ====== */}
      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaBanner}>
          <div>
            <h2>
              {FEATURES_PAGE.cta.title}
            </h2>
            <p>{FEATURES_PAGE.cta.subtitle}</p>
          </div>
          <div className={styles.ctaActions}>
            <a
              href={FEATURES_PAGE.cta.buttonLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnLarge}
              style={{ textAlign: 'center' }}
            >
              {FEATURES_PAGE.cta.button}
              <FaArrowRight className={styles.ctaButtonIcon} />
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default Features;
